import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const FanContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FanProvider = ({ children }) => {
  const [fan, setFan] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('fan_token'));
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchFan();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchFan = async () => {
    try {
      const response = await axios.get(`${API_URL}/fan/me`);
      setFan(response.data);
      fetchFavorites();
    } catch (error) {
      console.error('Failed to fetch fan:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API_URL}/fan/favorites`);
      setFavorites(response.data.map(m => m.id));
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    }
  };

  const register = async (email, name) => {
    const response = await axios.post(`${API_URL}/fan/register`, { email, name });
    const { access_token } = response.data;
    localStorage.setItem('fan_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    await fetchFan();
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('fan_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setFan(null);
    setFavorites([]);
  };

  const toggleFavorite = async (mixId) => {
    if (!token) return false;
    try {
      const response = await axios.post(`${API_URL}/mixes/${mixId}/like`);
      if (response.data.status === 'liked') {
        setFavorites([...favorites, mixId]);
      } else {
        setFavorites(favorites.filter(id => id !== mixId));
      }
      return true;
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      return false;
    }
  };

  const isFavorite = (mixId) => favorites.includes(mixId);

  return (
    <FanContext.Provider value={{ 
      fan, token, loading, register, logout, 
      toggleFavorite, isFavorite, favorites 
    }}>
      {children}
    </FanContext.Provider>
  );
};

export const useFan = () => {
  const context = useContext(FanContext);
  if (!context) {
    throw new Error('useFan must be used within a FanProvider');
  }
  return context;
};
